import React, { useEffect, useState } from 'react';
import { faChartLine, faCog, faInfo } from '@fortawesome/pro-solid-svg-icons';

import { Col, Container, Row } from '../../../grid';
import Icon from '../../atoms/icon';
import { Tabs, Tab } from '../../molecules/tab';
import ErrorBoundary from '../../molecules/error-boundary';
import Card from '../../organisms/card';
import { LoadingBlock } from '../../organisms/loading/progress';
import { AppKey } from '../../../libs/apps';
import { TriageCategory, TriagedApp } from '../../../contexts/triage/types';

import ProgramData from '../patient/data';
import EditSurvey from './edit-survey';
import { useGetSurvey } from '../../../libs/survey';
import { Terms, useMetaData } from '../../../contexts/metadata';
import { FormatDate, TIME_FORMATS } from '../../../libs/time';
import { ISurveyQual } from '../../../libs/apps/surveyqual';

export interface OtherProgramProps extends TriagedApp {
  name: string;
  appKey: AppKey;
}

/**
 * Because the quality surveys are not triaged, we have
 * to manually create the app data, and take some liberties
 * to fill in the gaps. Not everything is included in the
 * other program data, compared to regular triaging app data.
 *
 * Triaging is not performed on quality surveys, so we are
 * hardcoding the category to be "Success" or "Low-Risk"
 *
 * - No schedule or waiver -> do not include
 * - No alerts -> set to 0
 * - No statuses -> do not include
 */
export const surveyDefaultData: OtherProgramProps = {
  name: 'Quality Survey',
  appKey: 'surveyqual',
  category: TriageCategory.None,
  contactId: 99999,
  alert: {
    total: 0,
    unread: 0,
    active: 0
  },
  engagement: {
    rate: 0,
    responded: 0,
    total: 0
  }
};

interface UpcomingSureyCardProps {
  survey: ISurveyQual;
  surveyDateTime: string;
  terms: Terms;
  surveyType: string;
  isLoading: boolean;
}

const UpcomingSurveyCard: React.FC<UpcomingSureyCardProps> = ({
  survey,
  surveyDateTime,
  terms,
  surveyType,
  isLoading
}): JSX.Element => {
  const now = new Date();
  const surveyDate = new Date(survey?.time);
  if (isLoading) {
    return <LoadingBlock />;
  }
  return survey != null ? (
    surveyDate > now ? (
      // When next survey is in the future and surveys are active
      <Card>
        <h4 className="mt-0">
          <Icon icon={faInfo} className="mr-2" /> Upcoming Survey
        </h4>
        <p className="mb-0">
          {`The ${terms.patient.default} will receive their next quality survey on`}{' '}
          <strong>{surveyDateTime}</strong> {`via ${surveyType}.`}
        </p>
      </Card>
    ) : (
      // When most recent survey is in the past or unsubscribed
      <Card>
        <h4 className="mt-0">
          <Icon icon={faInfo} className="mr-2" /> No More Surveys
        </h4>
        <p>
          {`The ${terms.patient.default} will not be receiving any more surveys in the future.`}
        </p>
        <p>
          {`This could be because they unsubscribed to the quality survey texts or calls. If the ${terms.patient.default} wishes to opt back into the quality survey, they can either:`}
        </p>
        <ul>
          <li>
            For text messages, text "START" to the phone number listed in the
            "Phones" section under the "Manage Survey" tab
          </li>
          <li>{`For phone calls, the ${terms.patient.default} will have to be re-prescribed into a program`}</li>
        </ul>
        <p>
          <strong>Note:</strong>{' '}
          {`if there are no more surveys for this ${terms.patient.default}, then you will not be able to change the communication method for their quality surveys.`}
        </p>
        <p className="mb-0">
          If you think this message is being displayed in error, please reach
          out to support.
        </p>
      </Card>
    )
  ) : (
    // When no survey exists
    <Card>
      <h4 className="mt-0">
        <Icon icon={faInfo} className="mr-2" /> No Upcoming Survey
      </h4>
      <p className="mb-0">
        {`There is currently no quality survey for this ${terms.patient.default}. This could be because their enrolled programs do not support the quality survey, or because no quality surveys have been scheduled yet.`}
      </p>
    </Card>
  );
};

export const GetSurveyType = (type: string): string => {
  return type === 'message' ? 'text message' : 'phone call';
};

interface QualitySurveyProps {
  patientId: number;
  isSidebarExpanded: boolean;
  setIsSidebarExpanded: (isExpanded: boolean) => void;
}

/**
 * No event notes, because the quality survey does
 * not trigger events
 *
 * No response rate since we are not triaging
 *
 * Since the quality survey is independent of any program,
 * there is no need to include anything relating to
 * provider contacts
 */
const QualitySurvey: React.FC<QualitySurveyProps> = ({
  patientId,
  isSidebarExpanded,
  setIsSidebarExpanded
}): JSX.Element => {
  const { terms } = useMetaData();
  const { survey: surveyData, routes, isLoading } = useGetSurvey(patientId);
  const [survey, setSurvey] = useState<ISurveyQual>(null);
  const [surveyType, setSurveyType] = useState<string>('');
  const method = GetSurveyType(surveyData?.type);
  const format = TIME_FORMATS.DATE + ' ' + TIME_FORMATS.TIME_TZ;
  const surveyDateTime = FormatDate(surveyData?.time, format, surveyData?.tz);
  // Initialize method and survey state
  useEffect(() => {
    if (method) {
      setSurveyType(method);
    }
    if (surveyData) {
      setSurvey(surveyData);
    }
  }, [method, surveyData]);
  return (
    <Container fluid className="bg-white pb-4">
      <div>
        <Container fluid>
          <Row align="center" justify="start">
            <Col width="auto">
              <h3>{surveyDefaultData.name}</h3>
            </Col>
          </Row>
        </Container>
      </div>
      <Tabs id="program" renderActiveTabPanelOnly>
        <Tab
          id={`${surveyDefaultData.appKey}-data`}
          title={
            <span>
              <Icon className="mr-2 text-primary" icon={faChartLine} /> Survey
              Data
            </span>
          }
          className="px-2-important"
          panel={
            <>
              <UpcomingSurveyCard
                survey={survey}
                surveyDateTime={surveyDateTime}
                terms={terms}
                surveyType={surveyType}
                isLoading={isLoading}
              />
              <ProgramData
                appKey={surveyDefaultData.appKey}
                isSidebarExpanded={isSidebarExpanded}
                setIsSidebarExpanded={setIsSidebarExpanded}
                chartReflowTrigger={false}
              />
            </>
          }
        />
        <Tab
          id={`${surveyDefaultData.appKey}-prescription`}
          title={
            <span>
              <Icon className="mr-2 text-primary" icon={faCog} /> Manage Survey
            </span>
          }
          panel={
            <ErrorBoundary>
              <EditSurvey
                survey={survey}
                routes={routes}
                setSurvey={setSurvey}
                setSurveyType={setSurveyType}
              />
            </ErrorBoundary>
          }
        />
      </Tabs>
    </Container>
  );
};

export default QualitySurvey;
